import { HStack, Pressable, Text } from '@gluestack-ui/themed';

import {
  PATROL_LAB_ACTORS,
  PATROL_LAB_PREFLIGHT_STATES,
  PATROL_LAB_STAGES,
  serializePatrolLabQuery,
  type PatrolLabActor,
  type PatrolLabPreflightState,
  type PatrolLabQuery,
  type PatrolLabStage,
} from '@/visual-lab/chapters/patrol/parsePatrolLabQuery';

const STAGE_LABEL: Record<PatrolLabStage, string> = {
  briefing: 'Briefing',
  wall: 'Wall',
  observe: 'Observe',
  appear: 'Appear',
  engage: 'Engage',
  help: 'Needs help',
  preflight: 'Pre-flight',
};

const PREFLIGHT_STATE_LABEL: Record<PatrolLabPreflightState, string> = {
  waiting: 'Waiting',
  ready: 'Ready',
  mixed: 'Mixed',
  retakeDialog: 'Retake dialog',
  retakeRequested: 'Retake requested',
  cleared: 'Cleared',
  late: 'Late student',
};

const ACTOR_LABEL: Record<PatrolLabActor, string> = {
  supervisor: 'Supervisor',
  studentA: 'Harry',
  studentB: 'Hermione',
};

const ORAL_EXAM_LAB_PATH = '/visual-lab/oral-exam';

function Chip({
  active,
  label,
  testID,
  onPress,
}: {
  active: boolean;
  label: string;
  testID: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      px="$1.5"
      py="$0.5"
      borderRadius="$sm"
      bg={active ? '$bgBrandSubtle' : 'transparent'}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      <Text
        size="xs"
        color={active ? '$textHeading' : '$textSubtle'}
        fontWeight={active ? 'semibold' : 'normal'}
      >
        {label}
      </Text>
    </Pressable>
  );
}

type PatrolLabHeaderControlsProps = {
  query: PatrolLabQuery;
  onNavigate: (path: string) => void;
};

/** Compact stage × actor switcher for the Visual Lab header. */
export function PatrolLabHeaderControls({
  query,
  onNavigate,
}: PatrolLabHeaderControlsProps) {
  const go = (next: Partial<PatrolLabQuery>) => {
    onNavigate(`${ORAL_EXAM_LAB_PATH}${serializePatrolLabQuery({ ...query, ...next })}`);
  };

  const onPreflight = query.stage === 'preflight';
  const onBriefing = query.stage === 'briefing';
  const observeStripActive = query.stage === 'observe'
    && query.actor === 'supervisor'
    && !query.hand
    && query.submitted === undefined;
  const observeHandActive = query.stage === 'observe'
    && query.actor === 'supervisor'
    && query.hand === 'hermione';
  const wallSubmittedActive = query.stage === 'wall'
    && query.actor === 'supervisor'
    && query.submitted === 2;

  return (
    <HStack alignItems="center" space="sm" flexShrink={1} flexWrap="wrap">
      <HStack alignItems="center" space="xs" testID="patrol-lab-stage-switcher">
        {PATROL_LAB_STAGES.map((stage) => (
          <Chip
            key={stage}
            active={query.stage === stage}
            label={STAGE_LABEL[stage]}
            testID={`patrol-lab-stage-${stage}`}
            onPress={() => go(
              stage === 'preflight'
                ? {
                  stage,
                  state: query.state ?? 'ready',
                  hand: undefined,
                  submitted: undefined,
                }
                : { stage, hand: undefined, submitted: undefined },
            )}
          />
        ))}
      </HStack>
      {onPreflight ? (
        <>
          <Text size="xs" color="$borderLight">|</Text>
          <HStack alignItems="center" space="xs" testID="patrol-lab-preflight-state-switcher">
            {PATROL_LAB_PREFLIGHT_STATES.map((state) => (
              <Chip
                key={state}
                active={(query.state ?? 'ready') === state}
                label={PREFLIGHT_STATE_LABEL[state]}
                testID={`patrol-lab-preflight-state-${state}`}
                onPress={() => go({ stage: 'preflight', state })}
              />
            ))}
          </HStack>
        </>
      ) : !onBriefing ? (
        <>
          <Text size="xs" color="$borderLight">|</Text>
          <HStack alignItems="center" space="xs" testID="patrol-lab-actor-switcher">
            {PATROL_LAB_ACTORS.map((actor) => (
              <Chip
                key={actor}
                active={query.actor === actor}
                label={ACTOR_LABEL[actor]}
                testID={`patrol-lab-actor-${actor}`}
                onPress={() => go(actor === 'supervisor'
                  ? { actor }
                  : {
                    actor,
                    question: query.question ?? 'sitting',
                    clock: query.clock ?? 'reading',
                  })}
              />
            ))}
          </HStack>
          <Text size="xs" color="$borderLight">|</Text>
          <HStack alignItems="center" space="xs" testID="patrol-lab-scenario-switcher">
            <Chip
              active={observeStripActive}
              label="Observe strip"
              testID="patrol-lab-scenario-observe-strip"
              onPress={() => go({
                stage: 'observe',
                actor: 'supervisor',
                hand: undefined,
                submitted: undefined,
              })}
            />
            <Chip
              active={observeHandActive}
              label="Hand · Hermione"
              testID="patrol-lab-scenario-observe-hand"
              onPress={() => go({
                stage: 'observe',
                actor: 'supervisor',
                hand: 'hermione',
                submitted: undefined,
              })}
            />
            <Chip
              active={wallSubmittedActive}
              label="Submitted ×2"
              testID="patrol-lab-scenario-wall-submitted"
              onPress={() => go({
                stage: 'wall',
                actor: 'supervisor',
                hand: undefined,
                submitted: 2,
              })}
            />
          </HStack>
        </>
      ) : null}
    </HStack>
  );
}
